import React, { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "config/authentication/azure/authConfig";
import { api, setAccessToken } from "../../../services/api/client";

export const ManualSyncModal = ({ show, onClose }) => {
  const { instance, accounts } = useMsal();
  const [mode, setMode] = useState("date");
  const [date, setDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [time, setTime] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!date || (mode === "datetime" && !time)) {
      setMessage({ type: "warning", text: "Please select a valid date and time if required." });
      return;
    }

    setIsSubmitting(true);
    setMessage(null);

    try {
      // Get access token
      const { accessToken } = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      setAccessToken(accessToken);

      if (mode === "date") {
        await api.post(`/api/utilization/missing-hours?date=${date}`);
        setMessage({ type: "success", text: "Missing hours synced successfully." });
      } else {
        const datetime = `${date} ${time}:00`;
        await api.post(`/api/utilization/manual-run?dateTime=${datetime}`);
        setMessage({ type: "success", text: `Manual run started for ${datetime}.` });
      }

      // Auto close after success
      setTimeout(() => {
        onClose();
        setMessage(null);
      }, 2000);
    } catch (error) {
      setMessage({ type: "error", text: "An error occurred while syncing data." });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal modal-blur fade show" style={{ display: "block" }} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-refresh me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4" />
                  <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" />
                </svg>
                Sync Meraki Data
              </h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={isSubmitting}></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {message && (
                  <div className={`alert alert-${message.type === "success" ? "success" : message.type === "warning" ? "warning" : "danger"}`}>
                    {message.text}
                  </div>
                )}

                {/* Mode Selection */}
                <div className="mb-3">
                  <label className="form-label">Sync Mode</label>
                  <div className="btn-group w-100" role="group">
                    <button
                      type="button"
                      className={`btn ${mode === "date" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setMode("date")}
                      disabled={isSubmitting}
                    >
                      Date Only
                    </button>
                    <button
                      type="button"
                      className={`btn ${mode === "datetime" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setMode("datetime")}
                      disabled={isSubmitting}
                    >
                      Date & Time
                    </button>
                  </div>
                  <small className="form-text text-muted">
                    {mode === "date"
                      ? "Sync all missing hours for the selected date"
                      : "Sync specific hour for the selected date and time"}
                  </small>
                </div>

                {/* Date Input */}
                <div className="mb-3">
                  <label className="form-label required">Select Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                {/* Time Input (conditional) */}
                {mode === "datetime" && (
                  <div className="mb-3">
                    <label className="form-label required">Select Hour</label>
                    <select
                      className="form-select"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      required
                      disabled={isSubmitting}
                    >
                      <option value="">Select hour</option>
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={`${i.toString().padStart(2, "0")}:00`}>
                          {i.toString().padStart(2, "0")}:00
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Syncing...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-refresh me-1" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                        <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4" />
                        <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" />
                      </svg>
                      Sync
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={!isSubmitting ? onClose : undefined}></div>
    </>
  );
};
